import { useState, useRef, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { reportError } from 'util/sentry';
import type { MultiServicePickerOption } from 'rest/service-picker';
import { fetchServices } from 'rest/service-picker';
import type { showErrorFlag } from 'state/actions/flags';
import { operationalEvent, AnalyticsTrackActions } from 'util/analytics/events';

import messages from './messages';

// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
// eslint-disable-next-line no-shadow
enum Action {
    Append,
    Replace,
}

export const fireTrackAnalytics = (action: AnalyticsTrackActions) => {
    operationalEvent({
        action,
        actionSubject: 'portalFetchServices',
        source: 'multiServicePicker',
    });
};

export const usePaginatedServiceOptions = (showError: typeof showErrorFlag) => {
    const [options, setOptions] = useState<MultiServicePickerOption[]>([]);
    const cursor = useRef<string | null>(null); // The end cursor/page number for the current search result
    const hasNextPage = useRef<boolean>(false); // Whether there are more options to load from fetchSearvices
    const prevSearchValue = useRef<string>(''); // The latest input value we entered in the search
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const searchServices = async (inputValue: string, action: Action) => {
        // Don't search if doesn't have next page
        if (!hasNextPage.current && action === Action.Append) {
            return;
        }

        setIsLoading(true);
        // Remove the cursor so that we could start search from first page. Set hasNextPage to true to ensure we are always searching
        if (action === Action.Replace) {
            cursor.current = null;
        }
        prevSearchValue.current = inputValue;

        try {
            const { services, endCursor, hasNextPage: hasMorePage } = await fetchServices(inputValue, cursor.current);

            cursor.current = endCursor;
            hasNextPage.current = hasMorePage;

            if (action === Action.Replace) {
                setOptions(services);
            } else if (action === Action.Append) {
                setOptions((prevOptions) => [...prevOptions, ...services]);
            }
            fireTrackAnalytics(AnalyticsTrackActions.FETCHED);
        } catch (err) {
            reportError(err);
            fireTrackAnalytics(AnalyticsTrackActions.FETCHED_FAILURE);

            showError(messages.errorTitle, messages.errorDescription);
            // We only remove all the available options if we need to replace. Otherwise, we still maintain previous options
            if (action === Action.Replace) {
                setOptions([]);
            }
            hasNextPage.current = false;
        } finally {
            setIsLoading(false);
        }
    };

    // Initial search
    useEffect(() => {
        // Disabling existing violations, should be fixed when revisited.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        searchServices(prevSearchValue.current, Action.Replace);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Search when user types input value in the search field
    const onInputChange = useCallback(
        (inputValue: string) => {
            if (inputValue !== prevSearchValue.current) {
                // Disabling existing violations, should be fixed when revisited.
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                searchServices(inputValue, Action.Replace);
            }
        },
        [searchServices]
    );

    const debouncedOnInputChange = useRef(debounce(onInputChange, 100));

    // Search when user scrolls to bottom of the scroll menu or when all the available options have been selected
    const fetchMoreOptions = useCallback(() => {
        // Disabling existing violations, should be fixed when revisited.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        searchServices(prevSearchValue.current, Action.Append);
    }, [searchServices]);

    return {
        isLoading,
        options,
        fetchMoreOptions,
        onInputChange: debouncedOnInputChange.current,
    };
};
